import {useCallback, useMemo, useState} from 'react';
import {z} from 'zod';
import type {
  NotificationRuleResponse,
  NotificationVariable,
} from 'src/api/ProactApiClient';
import type {
  NotificationRuleValidationErrors} from './notificationRuleSchema';
import {
  createNotificationRuleSchema
} from './notificationRuleSchema';

export interface UseNotificationRuleValidationResult {
  errors: NotificationRuleValidationErrors;
  validateForm: (data: NotificationRuleResponse) => boolean;
  clearErrors: () => void;
  clearFieldError: (fieldPath: keyof NotificationRuleValidationErrors) => void;
  hasErrors: boolean;
}

/**
 * Map Zod error paths to our error object keys
 */
const mapPathToErrorKey = (
  path: (string | number)[]
): keyof NotificationRuleValidationErrors | null => {
  const pathStr = path.join('.');

  if (pathStr === 'name') return 'name';
  if (pathStr === 'subject') return 'subject';
  if (pathStr === 'body') return 'body';
  if (pathStr === 'metadata.recipients') return 'recipients';
  if (pathStr === 'metadata.conditions') return 'conditions';
  if (pathStr === 'metadata.formDefinitionId') return 'formDefinitionId';
  if (pathStr === 'metadata.courseDefinitionId') return 'courseDefinitionId';
  if (pathStr === 'metadata.courseDefinitionContentInvariantId') return 'courseDefinitionContentInvariantId';

  return null;
};

/**
 * Custom hook for validating NotificationRuleResponse data using Zod.
 * Integrates with the existing useState pattern used in the form.
 *
 * @param availableTokens - List of available notification tokens for validation
 */
export const useNotificationRuleValidation = (
  availableTokens: NotificationVariable[]
): UseNotificationRuleValidationResult => {
  const [errors, setErrors] = useState<NotificationRuleValidationErrors>({});

  // Create schema with current available tokens
  const schema = useMemo(() => {
    return createNotificationRuleSchema(availableTokens);
  }, [availableTokens]);

  // Validate entire form
  const validateForm = useCallback(
    (data: NotificationRuleResponse): boolean => {
      try {
        schema.parse(data);
        setErrors({});
        return true;
      } catch (error) {
        if (error instanceof z.ZodError) {
          const newErrors: NotificationRuleValidationErrors = {};

          error.errors.forEach(err => {
            const key = mapPathToErrorKey(err.path);
            if (key && !newErrors[key]) {
              // Only set first error for each field
              newErrors[key] = err.message;
            }
          });

          setErrors(newErrors);
          return Object.keys(newErrors).length === 0;
        }
        return false;
      }
    },
    [schema]
  );

  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Clear error for a specific field
  const clearFieldError = useCallback(
    (fieldPath: keyof NotificationRuleValidationErrors) => {
      setErrors(prev => {
        if (prev[fieldPath]) {
          const {[fieldPath]: _removed, ...rest} = prev;
          return rest;
        }
        return prev;
      });
    },
    []
  );

  return {
    errors,
    validateForm,
    clearErrors,
    clearFieldError,
    hasErrors: Object.keys(errors).length > 0,
  };
};
